/**
 * Pure Helfer für Farb-Runs auf Textfeld-Inhalten.
 * Runs sind Zeichenbereiche [start, end) mit Farbe; Invariante nach applyColorRun:
 * sortiert und überlappungsfrei. Bewusst identisch in src/lib und
 * infra/lambda/shared (wie gridLayout/fitTextToCell dupliziert).
 */
export interface ColorRun {
  start: number;
  end: number;
  color: string;
}

export function applyColorRun(runs: ColorRun[], newRun: ColorRun): ColorRun[] {
  if (newRun.start >= newRun.end) return runs;
  const result: ColorRun[] = [];
  for (const r of runs) {
    if (r.end <= newRun.start || r.start >= newRun.end) { result.push(r); continue; }
    if (r.start < newRun.start) result.push({ ...r, end: newRun.start });
    if (r.end > newRun.end) result.push({ ...r, start: newRun.end });
  }
  result.push({ ...newRun });
  result.sort((a, b) => a.start - b.start);
  return result;
}

export function clipColorRuns(runs: ColorRun[], textLength: number): ColorRun[] {
  return runs
    .map((r) => ({ ...r, end: Math.min(r.end, textLength) }))
    .filter((r) => r.start < r.end && r.start < textLength);
}

export function segmentText(
  text: string,
  baseColor: string,
  runs?: ColorRun[],
): { text: string; color: string }[] {
  if (!text) return [];
  if (!runs || runs.length === 0) return [{ text, color: baseColor }];
  const sorted = clipColorRuns([...runs].sort((a, b) => a.start - b.start), text.length);
  const segments: { text: string; color: string }[] = [];
  let pos = 0;
  for (const r of sorted) {
    if (r.start > pos) segments.push({ text: text.slice(pos, r.start), color: baseColor });
    segments.push({ text: text.slice(r.start, r.end), color: r.color });
    pos = r.end;
  }
  if (pos < text.length) segments.push({ text: text.slice(pos), color: baseColor });
  return segments;
}
